<template>
    <div class="menubar" :class="editMode ? 'menubar__full' : ''" :style="{right: sidebarRight + 'px', top: sidebarTop + 'px'}">
        <VJoyWrapper justify="flex-end" class="menubar__edit-button">
            <VJoyIconButton v-if="!editMode" variant="main" label="edit" icon="pen" @click="triggerEditMode" />
        </VJoyWrapper>

        <div v-if="editMode">
            <VJoyWrapper>
                <VJoyIconButton label="check" icon="check" @click="triggerSave" />
                <VJoyIconButton label="cross" icon="cross" @click="cancelEditMode" />
            </VJoyWrapper>
            <VJoyWrapper class="menubar__items-list">
                <template v-for="(item, index) in items" :key="index">
                    <MenuItem :item="item" />
                </template>
            </VJoyWrapper>
            <VJoyWrapper>
                <VJoyButton label="check" icon="sendit" @click="triggerPublish">Publish</VJoyButton>
            </VJoyWrapper>
        </div>
    </div>

    <VJoyDialog ref="editLinkDialog" pattern-confirm confirm-text="Confirm" cancel-text="Cancel" @dialog:confirm="confirmLink">
        <template #dialog-header>
            <VJoyInput v-model="currentLink" label="Link" name="currentLink" />
        </template>
    </VJoyDialog>
    <VJoyDialog ref="addTableDialog" pattern-confirm confirm-text="Confirm" cancel-text="Cancel" @dialog:confirm="confirmTable">
        <template #dialog-header>
            <VJoyInput v-model="currentTableColumns" label="Number of columns" name="currentTableColumns" />
        </template>
    </VJoyDialog>
    <VJoyDialog ref="editImageDialog" pattern-confirm confirm-text="Confirm" cancel-text="Cancel" @dialog:confirm="confirmImage">
        <template #dialog-header>
            <VJoyInput v-model="currentImageAlt" label="alternative text for the image" name="currentImageAlt" />
            <VJoyInput v-model="currentImage" label="Url of image (you have to upload it somewhere first)" name="currentImage" />
        </template>
    </VJoyDialog>
</template>
<script setup lang="ts">
    import type {PropType} from 'vue';
    import {ref} from 'vue';
    import type {Editor} from '@tiptap/vue-3';
    import {VJoyWrapper, VJoyButton, VJoyDialog, VJoyInput, VJoyIconButton} from '@maltjoy/core-vue';
    import MenuItem from './MenuItem.vue';
    import type {Item} from '../Item';

    const emit = defineEmits<{
        'edit-mode-active': [value: boolean];
        'content-saved': [];
        'content-published': [];
    }>();

    const editLinkDialog = ref<InstanceType<typeof VJoyDialog>>();
    const addTableDialog = ref<InstanceType<typeof VJoyDialog>>();
    const editImageDialog = ref<InstanceType<typeof VJoyDialog>>();
    const editMode = ref(false);
    const currentLink = ref('');
    const currentImage = ref('');
    const currentImageAlt = ref('');
    const currentTableColumns = ref(3);

    const props = defineProps({
        editor: {type: Object as PropType<Editor>, required: true},
    });

    const triggerSave = () => {
        emit('content-saved');
        cancelEditMode();
    };

    const triggerPublish = () => {
        emit('content-published');
        cancelEditMode();
    };

    const triggerEditMode = () => {
        editMode.value = true;
        emit('edit-mode-active', editMode.value);
    };

    const cancelEditMode = () => {
        editMode.value = false;
        emit('edit-mode-active', editMode.value);
    };

    const sidebarRight = ref(0);
    const windowHeight = window.innerHeight;
    const sidebarTop = ref(windowHeight / 2);

    function setLink() {
        currentLink.value = props.editor.getAttributes('link').href;
        editLinkDialog.value?.show();
    }

    function confirmLink() {
        if (currentLink.value === '') {
            props.editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        props.editor.chain().focus().extendMarkRange('link').setLink({href: currentLink.value, target: '_blank'}).run();
        editLinkDialog.value?.hide();
        currentLink.value = '';
    }

    function setImage() {
        currentImage.value = props.editor.getAttributes('image').href;
        editImageDialog.value?.show();
    }
    function confirmImage() {
        if (currentImage.value === '') {
            return;
        }
        props.editor.commands.setImage({src: currentImage.value, alt: currentImageAlt.value});
        editImageDialog.value?.hide();
        currentImage.value = '';
        currentImageAlt.value = '';
    }

    function setTable() {
        addTableDialog.value?.show();
    }
    function confirmTable() {
        if (currentTableColumns.value === 0) {
            return;
        }
        props.editor.commands.insertTable({cols: currentTableColumns.value, rows: 2});
        addTableDialog.value?.hide();
        currentTableColumns.value = 3;
    }

    const items: Item[] = [
        {
            icon: 'bold',
            title: 'Bold',
            action: () => props.editor.chain().focus().toggleBold().run(),
            isActive: () => props.editor.isActive('bold'),
        },
        {
            icon: 'italic',
            title: 'Italic',
            action: () => props.editor.chain().focus().toggleItalic().run(),
            isActive: () => props.editor.isActive('italic'),
        },
        {
            icon: 'underline',
            title: 'Underline',
            action: () => props.editor.commands.toggleUnderline(),
            isActive: () => props.editor.isActive('underline'),
        },
        {
            icon: 'link',
            title: 'Link',
            action: () => setLink(),
            isActive: () => props.editor.isActive('link'),
        },
        {
            icon: 'format-clear',
            title: 'Clear Format',
            action: () => props.editor.chain().focus().clearNodes().unsetAllMarks().run(),
        },
        {
            icon: 'h-1',
            title: 'Heading 1',
            action: () => props.editor.chain().focus().toggleHeading({level: 1}).run(),
            isActive: () => props.editor.isActive('heading', {level: 1}),
        },
        {
            icon: 'h-2',
            title: 'Heading 2',
            action: () => props.editor.chain().focus().toggleHeading({level: 2}).run(),
            isActive: () => props.editor.isActive('heading', {level: 2}),
        },
        {
            icon: 'h-3',
            title: 'Heading 3',
            action: () => props.editor.chain().focus().toggleHeading({level: 3}).run(),
            isActive: () => props.editor.isActive('heading', {level: 3}),
        },
        {
            icon: 'h-4',
            title: 'Heading 4',
            action: () => props.editor.chain().focus().toggleHeading({level: 4}).run(),
            isActive: () => props.editor.isActive('heading', {level: 4}),
        },
        {
            icon: 'paragraph',
            title: 'Paragraph',
            action: () => props.editor.chain().focus().setParagraph().run(),
            isActive: () => props.editor.isActive('paragraph'),
        },
        {
            icon: 'list-unordered',
            title: 'Bullet List',
            action: () => props.editor.chain().focus().toggleBulletList().run(),
            isActive: () => props.editor.isActive('bulletList'),
        },
        {
            icon: 'list-ordered',
            title: 'Ordered List',
            action: () => props.editor.chain().focus().toggleOrderedList().run(),
            isActive: () => props.editor.isActive('orderedList'),
        },
        {
            icon: 'separator',
            title: 'Horizontal Rule',
            action: () => props.editor.chain().focus().setHorizontalRule().run(),
        },
        {},
        {},
        {
            icon: 'text-wrap',
            title: 'Hard Break',
            action: () => props.editor.chain().focus().setHardBreak().run(),
        },
        {
            icon: 'align-left',
            title: 'Align left',
            action: () => props.editor.chain().focus().setTextAlign('left').run(),
            isActive: () => props.editor.isActive({textAlign: 'left'}),
        },
        {
            icon: 'align-center',
            title: 'Align center',
            action: () => props.editor.chain().focus().setTextAlign('center').run(),
            isActive: () => props.editor.isActive({textAlign: 'center'}),
        },
        {
            icon: 'align-right',
            title: 'Align right',
            action: () => props.editor.chain().focus().setTextAlign('right').run(),
            isActive: () => props.editor.isActive({textAlign: 'right'}),
        },
        {
            icon: 'image-fill',
            title: 'Image',
            action: () => setImage(),
        },
        {
            icon: 'table-2',
            title: 'Table',
            action: () => setTable(),
        },
        {
            icon: 'delete-bin-fill',
            title: 'Delete table',
            action: () => props.editor.commands.deleteTable(),
            isActive: () => props.editor.isActive('table'),
        },
        {},
        {},
        {},

        {
            icon: 'arrow-go-back-line',
            title: 'Undo',
            action: () => props.editor.chain().focus().undo().run(),
        },
        {
            icon: 'arrow-go-forward-line',
            title: 'Redo',
            action: () => props.editor.chain().focus().redo().run(),
        },
    ];
</script>

<style lang="scss" scoped>
    .divider {
        background-color: var(--joy-color-neutral-50);
        height: 1.25rem;
        margin-left: 0.5rem;
        margin-right: 0.75rem;
        width: 1px;
    }

    .menubar {
        transition: all 0.2s ease-in-out;

        position: fixed;
        top: 50%;
        right: 0;
        transform: translateY(-50%);
        width: 300px;
        box-sizing: border-box;
        z-index: 100;

        &__edit-button {
            margin-right: var(--joy-core-spacing-5);
        }

        &__full {
            border: 1px solid var(--joy-color-neutral-30);
            border-radius: var(--joy-core-radius-3);
            padding: 1rem;
            background-color: var(--joy-color-neutral-20);
            box-shadow: var(--joy-core-elevation-2);
        }

        &__items-list {
            background-color: var(--joy-color-neutral-10);
            border: 1px solid var(--joy-color-neutral-30);
            border-radius: var(--joy-core-radius-3);
            padding: 0.25rem;
            margin-bottom: 20px;
        }

        &__items {
            align-items: center;
            background-color: var(--joy-color-neutral-10);
            border: 1px solid var(--joy-color-neutral-30);
            border-radius: var(--joy-core-radius-3);
            display: flex;
            flex: 0 0 auto;
            flex-wrap: wrap;
            padding: 0.25rem;
            margin-bottom: 20px;
        }
    }
</style>
